import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Neue Abteilung"
 * (`add-edit-department.component`). Gestartet ueber den
 * `<app-dialog-help-button>` im Dialog-Header (Pattern analog
 * `EMPLOYEE_ADD_HELP_TOUR`).
 *
 * Der Dialog ist ein FLACHES Formular (kein `mat-accordion`) → KEIN
 * `panelIndex`. Jeder Schritt zielt auf genau ein Feld (Per-Step-Spotlight),
 * daher keine Cues noetig. Bewusst getrennt von der setup-getriebenen
 * `DEPARTMENT_ADD_CONTEXTUAL_TOUR` — deren `onboarding:department.*`-Events
 * bleiben unangetastet.
 *
 * Kernschritt: "Benoetigte Qualifikationen" → die Grundlage, um spaeter
 * Personalbedarfe passgenau zu planen.
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

export const DEPARTMENT_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.DepartmentAddHelp,
  version: 1,
  title: 'DEPARTMENT_ADD_HELP_TOUR_TITLE',
  description: 'DEPARTMENT_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Organisations-Departments-Seite navigieren, dann den
  // Add-Button klicken. Der Anchor `add-department-button` wird in
  // `organization-header.component.ts:243` dynamisch je Tab gebunden — daher
  // ist die genaue Sub-Route Pflicht.
  launchContext: {
    route: '/organization/departments',
    opens: { openerAnchor: AnchorId.AddDepartmentButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'DEPARTMENT_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.DepartmentDialog,
          spotlightAnchor: AnchorId.DepartmentDialog,
          title: 'TUTORIAL.DEPARTMENT_ADD.WELCOME.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.WELCOME.CONTENT',
          placement: 'right',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'name',
          anchor: AnchorId.DepartmentNameField,
          spotlightAnchor: AnchorId.DepartmentNameField,
          title: 'TUTORIAL.DEPARTMENT_ADD.NAME.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.NAME.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'parent',
          anchor: AnchorId.ParentDepartmentSelect,
          spotlightAnchor: AnchorId.ParentDepartmentSelect,
          title: 'TUTORIAL.DEPARTMENT_ADD.PARENT.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.PARENT.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="parent-department-select"]'),
        },
        {
          id: 'skills',
          // Kernschritt: benoetigte Qualifikationen → Personalbedarf.
          anchor: AnchorId.DepartmentSkillsSelect,
          spotlightAnchor: AnchorId.DepartmentSkillsSelect,
          title: 'TUTORIAL.DEPARTMENT_ADD.SKILLS.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.SKILLS.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="department-skills-select"]'),
        },
        {
          id: 'description',
          anchor: AnchorId.DepartmentDescriptionField,
          spotlightAnchor: AnchorId.DepartmentDescriptionField,
          title: 'TUTORIAL.DEPARTMENT_ADD.DESCRIPTION.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.DESCRIPTION.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="department-description-field"]'),
        },
        {
          id: 'create',
          anchor: AnchorId.SaveDepartmentButton,
          spotlightAnchor: AnchorId.SaveDepartmentButton,
          title: 'TUTORIAL.DEPARTMENT_ADD.CREATE.TITLE',
          content: 'TUTORIAL.DEPARTMENT_ADD.CREATE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
